import { ArrowLongRightIcon } from '@fourthwall/icons'
import { isPositiveIntegerString, pluralize } from '@fourthwall/utils/dist/string'
import React, { useRef, useState } from 'react'
import { Icon } from '../../common/Icon'
import { useOutsideClick } from '../../hooks/useOutsideClick'
import { InputUI } from '../../ui'
import { Input } from '../Input'
import * as S from './DayRange.styled'
import type { Props, Value } from './types'

export const DayRange = ({
  value,
  name,
  fromLabel = 'From',
  toLabel = 'To',
  width,
  suggestion,
  error,
  submitCount,
  disabled = false,
  onChange,
  onValueChange,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isFromFocused, setIsFromFocused] = useState(false)
  const [isToFocused, setIsToFocused] = useState(false)
  const [from = '', to = ''] = value || []
  const ref = useRef<HTMLDivElement>(null)

  useOutsideClick(ref, () => {
    setIsFocused(false)
  })

  const formatValue = (value: string, isFocused: boolean) => {
    if (!value || isFocused) {
      return value
    }

    return `${value} ${pluralize(parseInt(value), 'day')}`
  }

  const parseValue = (value: Value): Value => {
    return [
      !value?.[0] || isPositiveIntegerString(value[0]) ? value[0] : from,
      !value?.[1] || isPositiveIntegerString(value[1]) ? value[1] : to,
    ]
  }

  const handleValueChange = (value: Value) => {
    const parsedValue = parseValue(value)

    if (onChange) {
      onChange(parsedValue)
    }
    if (onValueChange) {
      onValueChange(parsedValue)
    }
  }

  const renderInputs = () => {
    return (
      <>
        <S.InputWrapper>
          <Input
            name={name?.[0]}
            hideSpinner
            label={fromLabel}
            transparent
            value={formatValue(from, isFromFocused)}
            disabled={disabled}
            onFocus={() => setIsFromFocused(true)}
            onBlur={() => setIsFromFocused(false)}
            onValueChange={(newFrom) => handleValueChange([newFrom, to])}
          />
        </S.InputWrapper>
        <S.IconContainer>
          <Icon component={ArrowLongRightIcon} height={16} />
        </S.IconContainer>
        <S.InputWrapper>
          <Input
            name={name?.[1]}
            hideSpinner
            label={toLabel}
            transparent
            value={formatValue(to, isToFocused)}
            disabled={disabled}
            onFocus={() => setIsToFocused(true)}
            onBlur={() => setIsToFocused(false)}
            onValueChange={(newTo) => handleValueChange([from, newTo])}
          />
        </S.InputWrapper>
      </>
    )
  }

  return (
    <S.InputContainer
      onClick={!disabled ? () => setIsFocused(true) : undefined}
      ref={ref}
      data-testid="DayRange.Container"
    >
      <S.InputBoxContainer width={width}>
        <InputUI.Box $invalid={!!error} $disabled={disabled} $focused={isFocused}>
          <S.InputsContainer>{renderInputs()}</S.InputsContainer>
        </InputUI.Box>
      </S.InputBoxContainer>
      {!error && suggestion && <InputUI.Suggestion>{suggestion}</InputUI.Suggestion>}
      {!!error && <InputUI.Error errorTimestampKey={submitCount}>{error}</InputUI.Error>}
    </S.InputContainer>
  )
}
